import { useTranslation } from 'react-i18next';
import { DataDictionary } from '../types';
import i18n from '../i18n/config';
import { clearDictionaryCache } from '../hooks/useDataDictionary';

/**
 * 根據當前語言獲取數據字典的顯示值
 * @param dict 數據字典對象
 * @returns 當前語言對應的顯示值
 */
export const getDictDisplayValue = (dict: DataDictionary | undefined): string => {
  if (!dict) return '';

  // ponytail: 直接讀 i18n.language（與 React re-render 同步），避免 localStorage 時序問題
  const language = i18n.language || localStorage.getItem('language') || 'zh-TW';
  if (language === 'zh-TW') {
    return dict.valueChinese || dict.valueEnglish || dict.code || '';
  }
  return dict.valueEnglish || dict.valueChinese || dict.code || '';
};

/**
 * 根據當前語言獲取數據字典列表的顯示值
 * @param dicts 數據字典數組
 * @param code 要查找的 code
 * @returns 當前語言對應的顯示值，如果找不到則返回 code
 */
export const getDictValueByCode = (dicts: DataDictionary[], code: string | undefined): string => {
  if (!code) return '';
  
  const codeUpper = code.toUpperCase();
  const dict = dicts.find(d =>
    d.code === code ||
    d.valueEnglish === code ||
    d.code.toUpperCase() === codeUpper ||
    d.valueEnglish.toUpperCase() === codeUpper
  );
  if (!dict) return code;
  
  return getDictDisplayValue(dict);
};

/**
 * Translate a backend errorCode to the current locale.
 * Falls back to the backend message string if no i18n key exists.
 */
export const resolveApiError = (errorCode: string | undefined, message: string | undefined, t: (key: string) => string): string => {
  if (errorCode && i18n.exists(`apiError.${errorCode}`)) {
    return t(`apiError.${errorCode}`);
  }
  return message || t('common.error');
};

/**
 * Hook: 切換語言
 */
export const useLanguageSwitcher = () => {
  const { i18n } = useTranslation();
  
  const switchLanguage = (lng: 'zh-TW' | 'en') => {
    clearDictionaryCache();
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    localStorage.setItem('languageExplicit', '1');
  };
  
  return { switchLanguage, currentLanguage: i18n.language };
};

